/* eslint-disable @typescript-eslint/naming-convention */
import { invert } from 'lodash';
import { matchPath } from 'react-router';
import type { Selector } from 'reselect';

import type { Language, Page, SEO, Website, WebsiteConfig, WebsiteLanguages } from '@swf/cms';
import { Brand, Country, FeatureFlag, Site } from '@swf/core/enums';
import type { RootState, SelectorCreator, Slicer } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { RequestOptions } from '@swf/core/types/Request';
import { canUseDOM } from '@swf/core/utils';
import { formatOptionalParams } from '@swf/core/utils/paths';

import deriveMeetingBasedEventTypeId, {
  isAnimalRacingRoutingKey
} from '../../../swf-sports/utils/getMeetingBasedEventTypeId';
import { stripLocale } from '../../utils/stripLocale';

import type { Alias, CMSState } from './index';

type SiteSuffixMap = { [key in Site]: string };

const BrandCodes = {
  [Brand.PokerStars]: 'PS',
  [Brand.PokerStarsCasino]: 'SC',
  [Brand.BetStars]: 'BS',
  [Brand.FullTilt]: 'FT',
  [Brand.PokerStarsVegas]: 'VS',
  [Brand.SkyBet]: 'SKB',
  [Brand.FoxBet]: 'FXB'
};

const BrandDisplayNames: Partial<Record<Brand, string>> = {
  [Brand.PokerStarsCasino]: 'PokerStars Casino',
  [Brand.BetStars]: 'PokerStars Sports'
};

const selectors = (slice: Slicer<CMSState>, createSelector: SelectorCreator) => ({
  get() {
    return slice;
  },
  getAlternateLinks() {
    return createSelector(
      slice,
      (rootState: RootState) =>
        (select.location.stripLocale as Selector<RootState, string>)(rootState),
      ({ website }, strippedPathname) =>
        website?.alternate_links
          ?.filter(({ link: { excluded_paths: excludedPaths } }) => {
            if (excludedPaths?.length) {
              const joinedExcludedPaths = excludedPaths
                .map((ep) => ep.path.replace(/(^\/)|(\/$)/g, ''))
                .join('|');
              const startWithExcludedPath = new RegExp(`^/(${joinedExcludedPaths})/.*`, 'g');
              return !startWithExcludedPath.test(strippedPathname);
            }

            return true;
          })
          ?.map(({ link: { locale_code: localeCode, base_url: baseUrl, replaced_path_start } }) => {
            const linkPath =
              replaced_path_start?.path_start_to_replace && replaced_path_start?.new_path_start
                ? strippedPathname.replace(
                    new RegExp(`^${replaced_path_start.path_start_to_replace}`),
                    replaced_path_start.new_path_start
                  )
                : strippedPathname;

            return {
              localeCode,
              url: `${baseUrl}${linkPath}`
            };
          })
    );
  },
  getWebSiteBrandName() {
    return createSelector(
      (rootState: RootState) => select.cms.getWebsite(rootState),
      (website) => website?.brand || 'Unknown'
    );
  },
  getWebsiteBrandId() {
    return createSelector(
      (rootState: RootState) => select.cms.getWebSiteBrandName(rootState),
      (websiteBrand) => Brand[websiteBrand]
    );
  },
  getPathBrandId() {
    return createSelector(
      (rootState: RootState) => select.cms.getWebsiteBrandId(rootState),
      (rootState: RootState) => select.location.stripBaseURL(rootState),
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (websiteBrandId, pathname, siteId) => {
        if (
          [
            Brand.BetStars,
            Brand.PokerStars,
            Brand.PokerStarsCasino,
            Brand.PokerStarsVegas
          ].includes(websiteBrandId)
        ) {
          switch (true) {
            case pathname.startsWith('/poker/'):
              return Brand.PokerStars;
            case pathname.startsWith('/vegas/') && (siteId === Site.DE || siteId === Site.DESH):
              return Brand.PokerStarsCasino;
            case pathname.startsWith('/vegas/'):
              return Brand.PokerStarsVegas;
            case pathname.startsWith('/casino/'):
              return Brand.PokerStarsCasino;
            case pathname.startsWith('/sports/'):
              return Brand.BetStars;
          }
        }
        return websiteBrandId;
      }
    );
  },
  getBrandId() {
    return createSelector(
      slice,
      (rootState: RootState) => select.cms.getWebsiteBrandId(rootState),
      ({ pathBrand }, websiteBrandId) => pathBrand || websiteBrandId
    );
  },
  getBrandName(this: { getBrandId: Selector<RootState, Brand> }) {
    return createSelector(
      this.getBrandId,
      (brandId) => BrandDisplayNames[brandId] ?? invert(Brand)[brandId]
    );
  },
  getBrandCode() {
    return createSelector(
      (rootState: RootState) => select.cms.getBrandId(rootState),
      (brandId) => BrandCodes[brandId]
    );
  },
  getCacheControl() {
    return slice((state) => state.cacheControl);
  },
  getCanonicalLink() {
    return slice((state) => state.website?.canonical_link);
  },
  getContent() {
    return slice(({ content }) => content);
  },
  getContentItem() {
    return createSelector(
      (rootState: RootState) => select.cms.getContent(rootState),
      (rootState: RootState, uid: string) => uid,
      (content, uid) => content[uid]
    );
  },
  getSEO() {
    return createSelector(
      (rootState: RootState) => select.cms.get(rootState),
      ({ page, content }) => {
        const refs = page?.content[0].seo;
        return refs?.length ? (content[refs[0].uid] as SEO) : undefined;
      }
    );
  },
  getDomain() {
    return createSelector(
      slice,
      (rootState: RootState) => select.location.getHostname(rootState),
      ({ website }, hostname) =>
        (website?.domains.find((domain) => hostname.startsWith(domain)) ||
          website?.domains[0]) as string
    );
  },
  getLegalAge() {
    return slice(({ website }) => website?.legal_age);
  },
  getMatchingAlias() {
    return createSelector(
      (rootState: RootState, aliases: Alias[]) => ({
        rootState,
        aliases
      }),
      ({ rootState, aliases }) => aliases.find((alias) => select.cms.matchAlias(rootState, alias))
    );
  },
  getNotificationSubscriptionPath() {
    return createSelector(
      (rootState: RootState) => select.locale.get(rootState),
      (rootState: RootState) => select.device.getId(rootState),
      (rootState: RootState) => select.account.getLicense(rootState),
      (rootState: RootState) => select.account.getUserId(rootState),
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (locale, device, license, userId, site) =>
        `/user/queue/starsweb/notifications/${locale}/${device}/${license}/${site}/${userId}`
    );
  },
  getFaviconURL() {
    return slice((state) => state.website?.favicon?.url);
  },
  getPage() {
    return slice((state) => state.page);
  },
  getPageTitle: () =>
    createSelector(
      (rootState: RootState) => select.cms.getSEO(rootState)?.title,
      (rootState: RootState) => select.cms.getPage(rootState)?.title,
      (seoTitle, pageTitle) => seoTitle ?? pageTitle
    ),
  getPageLoaded() {
    return slice((state) => state.pageLoaded);
  },
  getRedirect() {
    return slice((state) => state.redirect);
  },
  getRedirectTarget() {
    return slice(({ redirect, content }) => {
      const refs = redirect?.website;
      if (redirect?.to) return redirect.to;
      if (refs?.length) return (content[refs[0].uid] as Website).domains[0];
    });
  },
  getGeoRedirect() {
    return slice((state) => state.geo_redirect);
  },
  getGeoRedirectTarget() {
    return slice(({ geo_redirect, content }) => {
      const refs = geo_redirect?.redirect_website;
      const page = geo_redirect?.to_page;
      if (page?.length && refs?.length) {
        return (content[refs[0].uid] as Website).domains[0] + (content[page[0].uid] as Page).url;
      }
      if (refs?.length) return (content[refs[0].uid] as Website).domains[0];
    });
  },
  getRegion() {
    return slice((state) => state.website?.region || undefined);
  },
  getRequestOptions() {
    return createSelector(
      slice,
      (rootState: RootState) => select.cms.getWebsiteBrandId(rootState),
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (rootState: RootState) => select.cms.getRegion(rootState),
      (rootState: RootState) => select.country.getCountry(rootState),
      (rootState: RootState) => select.device.get(rootState),
      (rootState: RootState) => select.currency.get(rootState),
      (rootState: RootState) => select.locale.get(rootState),
      (rootState: RootState) => select.session.get(rootState),
      (rootState: RootState) => select.location.get(rootState),
      (
        cms,
        websiteBrand,
        site,
        region,
        country,
        deviceState,
        currency,
        locale,
        session,
        { href, origin, host }
      ) => {
        const requestMeta: RequestOptions['meta'] = { locale, currency };
        const headers: Record<string, string> = { referer: href };

        if (!canUseDOM) {
          const { device, os, embedded } = deviceState;

          if (cms.website) Object.assign(requestMeta, { site, websiteBrand });

          Object.assign(requestMeta, { device, country, os, embedded: embedded ? 1 : 0 });
          headers['x-forwarded-host'] = host;
          headers.origin = origin;
          if (region) requestMeta.region = region;
        }

        if (session.token) headers.Authorization = `StarsWeb-Session ${session.token}`;
        return { meta: requestMeta, headers };
      }
    );
  },
  getAliases() {
    return slice((state) => state.aliases || {});
  },
  getSiteId() {
    return slice((state) => Site[state.website?.site || 'COM'] as Site);
  },
  getSiteName() {
    return slice((state) => state.website?.site || 'COM');
  },
  getStarsDomain() {
    return createSelector(
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (id: number) =>
        ({
          [Site.IT]: 'starsaccount.it',
          [Site.GR]: 'starsaccount.gr',
          [Site.FR]: 'starsaccount.fr',
          [Site.ES]: 'starsaccount.es',
          [Site.DK]: 'starsaccount.dk',
          [Site.EU]: 'starsaccount.eu',
          [Site.UK]: 'starsaccount.uk',
          [Site.BG]: 'starsaccount.bg',
          [Site.DESH]: 'sh.starsaccount.de',
          [Site.RO]: 'starsaccount.ro',
          [Site.EE]: 'starsaccount.ee',
          [Site.CZ]: 'starsaccount.cz',
          [Site.BE]: 'starsaccount.be',
          [Site.RUSOPM]: 'starsaccount.net',
          [Site.PT]: 'starsaccount.pt',
          [Site.SE]: 'starsaccount.se',
          [Site.NET]: 'starsaccount.net',
          [Site.USPA]: 'starsaccountpa.com',
          [Site.USNJ]: 'pokerstarscasinonj.com',
          [Site.USMI]: 'pokerstarscasinomi.com',
          [Site.NL]: 'pokerstars.nl'
        }[id] || 'starsaccount.com')
    );
  },
  getCashierHref() {
    const siteSuffixMap: SiteSuffixMap = {
      [Site.COM]: '',
      [Site.NET]: '',
      [Site.IT]: 'it',
      [Site.FR]: '',
      [Site.EE]: 'ee',
      [Site.BE]: 'be',
      [Site.ES]: 'es',
      [Site.DK]: 'dk',
      [Site.EU]: 'eu',
      [Site.USNJ]: 'usnj',
      [Site.UK]: 'uk',
      [Site.BG]: 'bg',
      [Site.DESH]: '',
      [Site.RO]: 'ro',
      [Site.PT]: 'pt',
      [Site.CZ]: 'cz',
      [Site.RUSO]: '',
      [Site.RUSOPM]: '',
      [Site.IN]: '',
      [Site.ASIA]: '',
      [Site.RU]: '',
      [Site.USPA]: '',
      [Site.SE]: 'se',
      [Site.CH]: '',
      [Site.ARBA]: '',
      [Site.USCO]: 'uspa',
      [Site.DE]: '',
      [Site.USMI]: 'uspa',
      [Site.NL]: '',
      [Site.CAON]: '',
      [Site.GR]: ''
    };

    return createSelector(
      (rootState: RootState) => select.device.isEmbedded(rootState),
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (isEmbedded, site) =>
        isEmbedded ? `pokerstars${siteSuffixMap[site]}://cashier/open` : undefined
    );
  },
  getWebsite() {
    return slice(({ website }) => website);
  },
  getWebsiteLanguages() {
    return slice(({ website, content }) => {
      const refs = website?.website_languages;
      const websiteLanguages = !!refs?.length && (content[refs[0].uid] as WebsiteLanguages);
      if (!websiteLanguages) return [];
      return websiteLanguages.languages.map((item) => (content[item.uid] as Language).short_code);
    });
  },
  getWebsiteLaunchDate() {
    return slice(({ website, content }) => {
      const refs = website?.website_config;
      const websiteConfig = !!refs?.length && (content[refs[0].uid] as WebsiteConfig);
      return websiteConfig ? websiteConfig.isp_launch_date : undefined;
    });
  },
  getMenu() {
    return slice(({ menu, depth }) => ({ menu, depth }));
  },
  hasMultiBrandCountry() {
    return createSelector(
      slice,
      (rootState: RootState) => select.feature.check(rootState, FeatureFlag.SkyBetSports),
      (rootState: RootState) => select.session.isAuthenticated(rootState),
      ({ website }, skyBetSports, authenticated) =>
        website &&
        skyBetSports &&
        website.site !== 'IT' &&
        (!authenticated || ['EU', 'COM'].includes(website.site))
    );
  },
  isAuthRequired() {
    return createSelector(
      (rootState: RootState) => select.location.getPathname(rootState),
      (rootState: RootState) => select.cms.getWebsite(rootState),
      (pathname, website) => website?.auth_required_paths.includes(pathname)
    );
  },
  isSiteUS() {
    return createSelector(
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (site) => [Site.USMI, Site.USNJ, Site.USPA, Site.USCO].includes(site)
    );
  },
  isIndefiniteExclusionAllowed(this: {
    getSiteId: Selector<RootState, Site>;
    showVegasContent: Selector<RootState, boolean>;
  }) {
    return createSelector(
      this.getSiteId,
      this.showVegasContent,
      (site, showVegasContent) => [Site.COM, Site.EU, Site.NL].includes(site) && !showVegasContent
    );
  },
  isVegasBrand() {
    return createSelector(
      (rootState: RootState) => select.cms.getBrandId(rootState),
      (brandId) => brandId === Brand.PokerStarsVegas
    );
  },
  getActiveAlias() {
    return createSelector(
      (rootState: RootState) => select.cms.getPage(rootState),
      (rootState: RootState) => select.cms.getAliases(rootState),
      (page, aliases) =>
        Object.keys(aliases).find(
          (key) =>
            page?.url && formatOptionalParams(aliases[key]) === formatOptionalParams(page?.url)
        )
    );
  },
  matchAlias() {
    return createSelector(
      (rootState: RootState, alias: Alias | undefined) => alias,
      (rootState: RootState) => select.cms.getUrlPathname(rootState),
      (rootState: RootState) => select.cms.getPage(rootState),
      (rootState: RootState) => select.cms.getAliases(rootState),
      (rootState: RootState) => select.cms.getActiveAlias(rootState),
      (paramAlias, pathname, page, aliases, activeAlias) => {
        const alias = paramAlias || page?.alias || activeAlias;
        return alias
          ? matchPath(pathname, { path: aliases[alias], exact: true, strict: false })
          : null;
      }
    );
  },
  showVegasContent(this: { getCountry: Selector<RootState, string> }) {
    return createSelector(
      (rootState: RootState) => select.cms.getSiteId(rootState),
      (rootState: RootState) => select.country.getCountry(rootState),
      (rootState: RootState) => rootState.account?.country,
      (site, geoCountry, accountCountry) =>
        site === Site.DESH ||
        site === Site.DE ||
        (site === Site.EU && (accountCountry || geoCountry) === Country.Germany)
    );
  },
  showOntarioContent(this: { getSiteId: Selector<RootState, Site> }) {
    return createSelector(this.getSiteId, (site) => site === Site.CAON);
  },
  getSportsUrlParams: () =>
    createSelector(
      (rootState: RootState) => select.cms.matchAlias(rootState, undefined),
      (rootState: RootState) => select.cms.getPage(rootState)?.alias,
      (match, alias) => {
        const urlParams = {} as Record<string, string>;
        if (match) {
          const { marketId } = match.params as Record<string, string>;
          const meetingBasedEventTypeId =
            isAnimalRacingRoutingKey(alias) && deriveMeetingBasedEventTypeId(alias).toString();
          Object.assign(urlParams, {
            ...match.params,
            ...(meetingBasedEventTypeId && { eventTypeId: meetingBasedEventTypeId }),
            ...(marketId && { marketId: marketId.replace('-', '.') })
          });
        }
        return urlParams;
      }
    ),
  // TODO: CMS-migration - investigate pathname as backup in SW-13631
  // see https://git.pyrsoftware.ca/stash/projects/STARSWEB/repos/stars-web-client/commits/43da26e8873b76c42b2b785fffa129c1240eee83#packages/swf-core/models/page.ts
  getRedirectUrl() {
    return createSelector(
      (rootState: RootState) => select.localSettings.get(rootState),
      ({ redirectTo }) => redirectTo || '/'
    );
  },
  getUrlPathname() {
    return createSelector(
      (rootState: RootState) => select.cms.get(rootState),
      (rootState: RootState) => select.locale.get(rootState),
      ({ url }, locale) => stripLocale(url?.pathname || '/', locale)
    );
  },
  getUrlParams: () =>
    createSelector(
      (rootState: RootState) => select.cms.matchAlias(rootState, undefined),
      (match) => (match ? match.params : {}) as Record<string, string>
    ),
  isIspRouting: () =>
    createSelector(
      (rootState: RootState) => select.feature.check(rootState, FeatureFlag.ISPRouting),
      (rootState: RootState) => select.location.getQuery(rootState),
      (ispRouting, { siteId }) => ispRouting || siteId === 'isp'
    )
});

export default selectors;
